import React, { ReactElement, useContext, useEffect, useRef, useState } from "react";
import { Box, Theme } from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useHistory } from "react-router-dom";
import { AxiosResponse } from "axios";
import CustomProgress from "../../../components/Alert/CustomProgress";
import LinearIndeterminate from "../../../components/Loading/LinearIndeterminate";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import { columnsDictionary, TestRequestOverviewColumnLabels } from "./TestRequestsOverviewColumns";
import windowOpenHelper from "../../../helper/WindowHelper";
import { TestRequestSearchResult, TestRequestsResponseObject } from "../../../api/models/TestRequest";
import useTableData from "./useTableData.hook";
import { setRowsPerPage, SortDirection } from "../../../helper/UserSettingsHelper";
import PagedResponse from "../../../api/models/PagedResponse";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { TestRequestAdvancedSearchRQ } from "../../../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import WaitingFilter from "../../../api/models/WaitingFilter";
import PrioritySearchTerm from "../../../api/models/test-request/PrioritySearchTerm";

interface TestRequestTableProps {
  permission: boolean;
  refresh: boolean;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  waiting?: boolean;
  waitingForCustomer?: WaitingFilter;
  statusFilterQuery?: string;
  fetchTestRequests: () => Promise<AxiosResponse<PagedResponse<TestRequestSearchResult>>>;
  sync: <T>(fetch: () => Promise<T>) => Promise<T>;
  basicSearchTerms?: string[];
  prioritySearchTerm?: PrioritySearchTerm;
  advancedSearch?: TestRequestAdvancedSearchRQ;
  retriggerSearch?: boolean;
  pageIndex: number;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  sortColumn: TestRequestOverviewColumnLabels;
  setSortColumn: React.Dispatch<React.SetStateAction<TestRequestOverviewColumnLabels>>;
  sortDirection: SortDirection;
  setSortDirection: React.Dispatch<React.SetStateAction<SortDirection>>;
  updateHasResults?: (hasResult: boolean) => void;
  searchArchive?: boolean;
  changeRequestPriority?: (requestId: string, priority: string) => void;
}

const TestRequestsOverviewTable = ({
  permission: editPriority,
  refresh,
  isLoading,
  setIsLoading,
  waiting,
  waitingForCustomer,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  fetchTestRequests,
  basicSearchTerms,
  prioritySearchTerm,
  statusFilterQuery,
  advancedSearch,
  retriggerSearch,
  sync,
  updateHasResults,
  searchArchive,
  changeRequestPriority,
}: TestRequestTableProps): ReactElement => {
  const theme = useTheme<Theme>();
  const screenSizeLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const history = useHistory();
  const doFetch = useRef(!!basicSearchTerms || !!advancedSearch || !!searchArchive || !!prioritySearchTerm);
  const { labOrUser } = useContext(DashboardContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const [testRequests, setTestRequests] = useState<TestRequestsResponseObject>({
    data: [],
    pageIndex,
    pageSize,
    totalData: 0,
    totalPages: 0,
  });
  const [apiCalled, setApiCalled] = useState(false);
  const { columns, rows } = useTableData(testRequests.data, true, editPriority);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setIsLoading(true);
    setPageIndex(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const rowsPerPage = parseInt(event.target.value, 10);
    setIsLoading(true);
    setPageSize(rowsPerPage);
    setRowsPerPage(rowsPerPage);
    setPageIndex(0);
  };

  const updateSortColumn = (newSortColumn: TestRequestOverviewColumnLabels): void => {
    if (sortColumn === newSortColumn) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortColumn(newSortColumn);
    }
  };

  const handleEdit = (request: CustomTableRowData): void => {
    localStorage.setItem("recentReqId", request?.requestId);
    localStorage.setItem("recentDialog", "");
    history.push(`/requests/${request?.requestId}`);
  };

  const handleMultiTab = (request: CustomTableRowData): void => {
    windowOpenHelper({
      url: `requests/${request.requestId}`,
    });
  };

  useEffect(() => {
    if (pageIndex > 0) {
      setPageIndex(0);
      doFetch.current = false;
    }
  }, [labOrUser, statusFilterQuery, basicSearchTerms, prioritySearchTerm]);

  useEffect(() => {
    setPageIndex(0);
  }, [advancedSearch]);

  useEffect(() => {
    if (doFetch.current) {
      setIsLoading(true);
      sync(() => fetchTestRequests())
        .then((res) => {
          setTestRequests(res.data as TestRequestsResponseObject);
          setIsLoading(false);
          setApiCalled(true);
          updateHasResults?.((res.data as TestRequestsResponseObject).data?.length > 0);
        })
        .catch(handleHttpError);
    }
    return () => {
      doFetch.current = true;
    };
  }, [
    refresh,
    sortColumn,
    sortDirection,
    pageIndex,
    pageSize,
    advancedSearch,
    labOrUser,
    retriggerSearch,
    statusFilterQuery,
    basicSearchTerms,
    prioritySearchTerm,
    waitingForCustomer,
  ]);

  if (isLoading && !apiCalled) {
    return <CustomProgress />;
  }

  return (
    <>
      <LinearIndeterminate loading={isLoading || waiting || false} />
      {apiCalled && testRequests.data?.length === 0 && <Box marginTop={1}>No results found.</Box>}
      {testRequests.data?.length > 0 && (
        <>
          <CustomTable
            boldIndex={1}
            columns={columns}
            columnExactWidths={{
              [columnsDictionary.requestId]: "11.5ch",
              [columnsDictionary.shortCode]: "23ch",
            }}
            excludedKeys={["id"]}
            actionColumnIndexes="ALL"
            hasFixedColumns={screenSizeLarge}
            formatDates
            reducePadding
            rows={rows}
            sortableColumns={{ all: true }}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            sortFunction={updateSortColumn}
            stickyIndex={2}
            handleEdit={handleEdit}
            handleMultiTab={handleMultiTab}
            onPriorityChange={editPriority ? changeRequestPriority : undefined}
            headDense
          />
          <TablePagination
            component="div"
            count={testRequests.totalData}
            page={pageIndex}
            onChangePage={handleChangePage}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </>
      )}
    </>
  );
};

export default TestRequestsOverviewTable;
